import { gettext } from '@eventbrite/i18n';
import { majorToMinor } from '@eventbrite/intl';
import countBy from 'lodash/countBy';
import escape from 'lodash/escape';
import find from 'lodash/find';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import head from 'lodash/head';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import partialRight from 'lodash/partialRight';
import pick from 'lodash/pick';

import {
    CHECKOUT_FORM_NAME,
    ORDER_ATTENDEE_WITH_ASSIGNED_UNIT_SHAPE_OBJECT,
    ORDER_TYPE_TRANSFER,
    PAYMENT_METHODS,
    ROYALTY_COMPONENT_NAME,
    TICKET_CLASS_CATEGORIES_BY_ORDER,
} from '../../constants';
import {
    ORDER_DISQUALIFICATIONS,
    ORDER_DISQUALIFICATIONS_FIELD_MAP,
    SEND_EMAIL_TO_ATTENDEES_FIELD,
    TaxExemptionStatus,
    TOS_CHECKBOX_FIELD,
} from '../../containers/checkout/constants';
import { OrderStatus } from '../../models/order';
import { getTotalDiscountingVirtualIncentives } from '../../utils/virtualIncentives';
import { getCurrency } from '../event';
import { createSelector } from '../utils/selector';
import { getSelectedVirtualIncentives } from '../virtualIncentive';
import {
    getAttendees,
    getAvailablePaymentMethods,
    getCurrencyFromOrder,
    hasExistingOrder,
    isAddAttendeesOrder,
} from './typedOrderSelectors';
import { getUnformattedOrderCost } from './getUnformattedOrderCost';

// Helpers
const getAssignedUnitsFromAttendees = (attendees) =>
    attendees.map((attendee) => attendee.assignedUnit);
const getAssignedUnitsFromFormattedAttendees = (attendees) =>
    getAssignedUnitsFromAttendees(attendees).filter((i) => i);

/**
 * @typedef {Object} Attendee
 * @property {Object} [assignedUnit] - Only for admission inventory
 * @property {String} team
 * @property {Object} costs
 * @property {String} resourceUri
 * @property {String} id
 * @property {String} changed
 * @property {String} created
 * @property {Number} quantity
 * @property {String} variantId
 * @property {String} profile
 * @property {Array} barcodes
 * @property {Array} answers
 * @property {Boolean} checkedIn
 * @property {Boolean} cancelled
 * @property {Boolean} refunded
 * @property {String} affiliate
 * @property {String} guestlistId
 * @property {String} invitedBy
 * @property {String} status
 * @property {String} ticketClassName
 * @property {String} deliveryMethod
 * @property {String} eventId
 * @property {String} orderId
 * @property {String} ticketClassId
 * @property {Array} survey
 * @property {Object} ticketClass
 */

const getOrderEligibility = (state) =>
    get(state, 'order.eligibility', {
        eligible: true,
        disqualifications: [],
    });

/**
 * Returns an array of attendees formatted for
 * @param {Object} state
 * @return {Array}
 */
export const getFormattedAttendeesWithAssignedUnit = (state) =>
    getAttendees(state).map((attendee) =>
        pick(
            attendee,
            Object.keys(ORDER_ATTENDEE_WITH_ASSIGNED_UNIT_SHAPE_OBJECT),
        ),
    );

/**
 * Returns an array of formatted assigned units for event attendees
 *
 * @param {Object} state
 * @returns {Array}
 */
export const getAssignedUnits = (state) =>
    getAssignedUnitsFromFormattedAttendees(
        getFormattedAttendeesWithAssignedUnit(state),
    );

/**
 * Returns an array of attendees for the event who have assigned units (ie have seat locations and
 * don't represent non-admission inventory)
 *
 * @param {Object} state
 * @returns {Array}
 */
export const getAttendeesWithAssignedUnits = (state) =>
    getFormattedAttendeesWithAssignedUnit(state).filter(
        (attendee) => attendee.assignedUnit,
    );

/**
 * Returns the assigned unit id, ie map seat location, for an attendee assigned unit
 *
 * @param {Object} state
 * @returns {String} Unit ID
 */
export const getFirstAssignedUnitIdFromOrder = (state) =>
    get(head(getAttendees(state)), 'assignedUnit.unitId', null);

/**
 * Returns true when there is an existing order ID and orderType is `transfer`
 *
 * @param {Object} state the application state
 */
export const isTransferredOrder = (state) =>
    hasExistingOrder(state) &&
    get(state, 'order.augmentedInfo.orderType') === ORDER_TYPE_TRANSFER;

/**
 * Returns true when it is a transferred order that was started by the org
 *
 * @param {Object} state the application state
 */
export const isOrganizerInitiatedTransferOrder = (state) =>
    isTransferredOrder(state) &&
    get(state, 'order.augmentedInfo.initiatedByOrganizer', false);

/**
 * Returns true when an organizer is managing an alien pre-created order (either an attendee transfer or adding an attendee)
 *
 * @param {Object} state the application state
 */
export const isOrganizerManagingAnAlienOrder = (state) =>
    isOrganizerInitiatedTransferOrder(state) || isAddAttendeesOrder(state);

/**
 * Returns true if the gross value cost of the order is > 0 and it is not an already existing order
 *
 * @param {*} state the application state
 */
export const isPaidOrder = (state) =>
    get(state, 'order.costs.gross.value', 0) > 0 && !isAddAttendeesOrder(state);

/**
 * Returns total gross value of the order
 *
 * @param {*} state the application state
 */
export const getTotalCostsFromGrossValue = (state) =>
    get(state, 'order.costs.gross.value', 0);

/**
 * Returns total gross major value of the order
 *
 * @param {*} state the application state
 */
export const getTotalCostsFromGrossMajorValue = (state) =>
    get(state, 'order.costs.gross.majorValue', 0);

/**
 * Selector to determine if the order cart has any attendees which makes the order ineligible for checkout.
 *
 */
export const getDisqualifiedAttendees = createSelector(
    getAttendees,
    getOrderEligibility,
    (attendees, orderEligibility) => {
        const { disqualifications } = orderEligibility;

        if (isEmpty(attendees)) {
            return [];
        }

        if (!orderEligibility.eligible) {
            const attendeesGroupByTicketClassId = groupBy(
                attendees,
                'ticketClass.id',
            );
            const disqualifiedAttendees = [];
            let problemAttendees;

            disqualifications.forEach(({ reason, ticketClassId }) => {
                const attendeesForTicket =
                    attendeesGroupByTicketClassId[ticketClassId];

                if (reason === ORDER_DISQUALIFICATIONS.BELOW_MINIMUM_QUANTITY) {
                    const numOfAttendeesForTicket = attendeesForTicket.length;

                    problemAttendees = attendeesForTicket.filter(
                        (attendee) =>
                            numOfAttendeesForTicket <
                            attendee.ticketClass.minimumQuantity,
                    );
                } else {
                    problemAttendees = attendeesForTicket;
                }

                disqualifiedAttendees.push(...problemAttendees);
            });

            return disqualifiedAttendees;
        }

        return [];
    },
);

const getDisqualifiedTicketClassIdsByReason = (state, filterReason) => {
    const { disqualifications } = getOrderEligibility(state);

    if (isEmpty(disqualifications)) {
        return [];
    }

    return disqualifications
        .filter(({ reason }) => reason === filterReason)
        .map(({ ticketClassId }) => ticketClassId);
};

const getBelowMiniumQuantityTicketClassIds = partialRight(
    getDisqualifiedTicketClassIdsByReason,
    ORDER_DISQUALIFICATIONS.BELOW_MINIMUM_QUANTITY,
);

/**
 * Selector to retrieve the formatted error message for the first carted ticket that doesn't meet the minimum quantity requirement
 */
export const getMinimumQuantityErrorMessage = createSelector(
    getBelowMiniumQuantityTicketClassIds,
    getDisqualifiedAttendees,
    (belowMiniumQuantityTicketClassIds, disqualifiedAttendees) => {
        const ticketClassFieldName =
            ORDER_DISQUALIFICATIONS_FIELD_MAP.BELOW_MINIMUM_QUANTITY;
        let errorMessage;

        if (
            !isEmpty(disqualifiedAttendees) &&
            !isEmpty(belowMiniumQuantityTicketClassIds)
        ) {
            // Find the first problem attendee, the error message will be shown for one problem at one time
            const problemAttendee = find(disqualifiedAttendees, (attendee) =>
                includes(
                    belowMiniumQuantityTicketClassIds,
                    get(attendee, 'ticketClass.id'),
                ),
            );

            if (problemAttendee) {
                errorMessage = gettext(
                    '%(ticketName)s quantity must be %(minQuantity)s or more.',
                    {
                        ticketName: `<b>${escape(
                            problemAttendee.ticketClass.name,
                        )}</b>`,
                        minQuantity:
                            problemAttendee.ticketClass[ticketClassFieldName],
                    },
                );
            }
        }

        return errorMessage;
    },
);

export const getDisableEmailToAttendees = (state) => {
    const sendEmailToAttendees = get(state, [
        'form',
        CHECKOUT_FORM_NAME,
        'values',
        SEND_EMAIL_TO_ATTENDEES_FIELD,
    ]);

    return isAddAttendeesOrder(state) && sendEmailToAttendees === false;
};

/**
 * Selector that can be used to retrieve the correct email configuration for an order
 * @param {*} state
 */
export const getEmailConfiguration = (state) => {
    const {
        app: {
            featureFlags: { shouldBlockMarketingEventbriteEmails } = {},
        } = {},
        form: {
            [CHECKOUT_FORM_NAME]: {
                values: { [TOS_CHECKBOX_FIELD]: tosCheckboxValue } = {},
            } = {},
        } = {},
    } = state;

    const shouldDisableEmailToAttendees = getDisableEmailToAttendees(state);
    const disableMarketingEmail =
        shouldBlockMarketingEventbriteEmails || shouldDisableEmailToAttendees;
    const disableConfirmationEmail = shouldDisableEmailToAttendees;

    return {
        accept_tos_order: !!tosCheckboxValue,
        ...(disableMarketingEmail || disableConfirmationEmail
            ? {
                  email_configuration: {
                      disable_new_user_email: disableMarketingEmail,
                      disable_confirmation_email: disableConfirmationEmail,
                      disable_pdf_ticket: disableMarketingEmail,
                      disable_newsletter_email: disableMarketingEmail,
                      disable_attendee_news_email: disableMarketingEmail,
                  },
              }
            : {}),
    };
};

/**
 * Selector that can be used to retrieve the current order token
 * @param {Object} state
 */
export const getOrderToken = (state) => get(state, 'tokens.orderToken');

const isSelectedWithShippingFee = ({ displayShippingFee, selectedQuantity }) =>
    selectedQuantity > 0 && displayShippingFee?.value;

export const haveSelectedTicketsShippingFee = (state) => {
    const { tickets } = state;

    if (isEmpty(tickets)) {
        return false;
    }

    return !!Object.values(tickets.ticketsById).find(
        ({ displayShippingFee, selectedQuantity, variants = [] }) => {
            if (isEmpty(displayShippingFee)) {
                return variants.find(isSelectedWithShippingFee);
            }

            return isSelectedWithShippingFee({
                displayShippingFee,
                selectedQuantity,
            });
        },
    );
};

const isTicketWithShippingFee = ({ displayShippingFee }) => {
    return displayShippingFee?.value;
};

export const isEventWithShippingFee = (state) => {
    const { tickets } = state;

    if (isEmpty(tickets)) {
        return false;
    }

    return !!Object.values(tickets.ticketsById).find(
        ({ displayShippingFee, variants = [] }) => {
            if (isEmpty(displayShippingFee)) {
                return variants.find(isTicketWithShippingFee);
            }
            return isTicketWithShippingFee({ displayShippingFee });
        },
    );
};

/**
 * Selector for checking if the checkout summary pane
 * should show the fee breakdown for multiple royalties.
 * This will return true if there are multiple royalties on a ticket.
 * @param {*} state
 * @returns {Boolean} true if tickets or order costs have royalties
 * @was `containers/pane/utils.js`
 */
export const hasMultipleRoyalties = (state) => {
    const { tickets, order: { costs: { feeComponents = [] } = {} } = {} } =
        state;
    let components = [];
    let royalties = [];

    if (!isEmpty(tickets)) {
        tickets.ticketIds.forEach((id) => {
            const { feeComponents, variants } = tickets.ticketsById[id];

            if (isEmpty(feeComponents)) {
                // in reserved or tiered inventory - get variant components
                variants.forEach(({ feeComponents }) => {
                    if (!isEmpty(feeComponents)) {
                        components = components.concat(feeComponents);
                    }
                });
            } else {
                components = components.concat(feeComponents);
            }
        });
        // Old royalty name and groupName should be equal.
        // For multiple royalties name and groupName should be different.
        // TODO: document and explain this with more test cases
        // Why does it not count when name === groupName
        royalties = components.filter(
            ({ name, groupName }) =>
                name.includes(ROYALTY_COMPONENT_NAME) && name !== groupName,
        );

        if (!isEmpty(royalties)) {
            return true;
        }
    }

    // For donation only - check order costs fee components
    // as fees on donation are calculated once order created
    // Old royalty internalName and name should be equal.
    // For multiple royalties name and internalName should be different.
    // TODO: document and explain this with more test cases
    // Why does it not count when name === internalName
    if (!isEmpty(feeComponents)) {
        royalties = feeComponents.filter(
            ({ name, internalName }) =>
                internalName.includes(ROYALTY_COMPONENT_NAME) &&
                name !== internalName,
        );

        if (!isEmpty(royalties)) {
            return true;
        }
    }

    return false;
};
/**
 * Given availablePaymentMethods this returns the available credit card types
 * eg. VISA, MASTERCARD
 * Returns [] if there's no credit card available
 * @param state - redux state
 * @returns {[]}
 */
export const getAvailableCreditCardTypes = (state) => {
    const { order: { availablePaymentMethods = [] } = {} } = state;

    const eventbritePaymentMethod = availablePaymentMethods.find(
        (pm) => pm.type === 'eventbrite',
    );

    if (!eventbritePaymentMethod) return [];

    const creditCardInstrumentType =
        eventbritePaymentMethod.instrumentTypes.find(
            (i) => i.type.trim() === PAYMENT_METHODS.CREDIT,
        );

    return creditCardInstrumentType ? creditCardInstrumentType.variants : [];
};

export const getOrderErrorProps = (state) => {
    const {
        order: { status, id },
    } = state;

    let externalPaymentDeclinedError = undefined;

    if (status === OrderStatus.declined) {
        externalPaymentDeclinedError = gettext(
            'There was a problem with your payment. Please choose another method and try again.',
        );
    }

    return {
        orderId: id,
        externalPaymentDeclinedError,
    };
};

export const hasVirtualIncentivesErrors = (state) =>
    !!get(state, 'order.hasVirtualIncentivesErrors', false);

export const getIsOrderUpdating = (state) => !!state.order?.isUpdating;

export const getbraintreeClientToken = (state) =>
    state.order.braintreeClientToken;

export const getTaxExemptionStatus = (state) =>
    state.order?.taxExemptionData?.status ?? TaxExemptionStatus.init;

export const getTotalAmount = (state) =>
    majorToMinor(
        getTotalDiscountingVirtualIncentives(
            getUnformattedOrderCost(state),
            getSelectedVirtualIncentives(state),
        ),
        getCurrencyFromOrder(state) ?? getCurrency(state),
    );

export const isApplePayAllowedInOrder = (state) =>
    !!getAvailablePaymentMethods(state)[0]?.instrumentTypes?.find(
        (paymentMethod) => paymentMethod.type === PAYMENT_METHODS.APPLE_PAY,
    );

export const isGooglePayAvailableAsPaymentMethod = (state) =>
    !!getAvailablePaymentMethods(state)[0]?.instrumentTypes?.find(
        (paymentMethod) => paymentMethod.type === PAYMENT_METHODS.GOOGLE_PAY,
    );

/**
 * Returns an object with selected delivery methods as keys and the total quantity of
 * tickets for each of those delivery methods as values
 * Example object returned: {'electronic': 1, 'will_call': 5}
 *
 * @param state state object includes {order: {attendees}}
 * @returns {Object}
 */
export const getAttendeeCountGroupBySelectedDeliveryMethod = createSelector(
    getAttendees,
    (attendees) => countBy(attendees, 'deliveryMethod'),
);

export const getAttendeeDeliveryMethodsPayloadForPlacingOrder = createSelector(
    isTransferredOrder,
    getAttendees,
    (isTransferredOrder, attendees) =>
        isTransferredOrder
            ? null
            : attendees.map(({ id, deliveryMethod }) => ({
                  attendee_id: id,
                  delivery_method: deliveryMethod,
              })),
);

export const getAttendeesSortedByTicketCategory = createSelector(
    getAttendees,
    (attendees) => {
        const groupAttendeesByTicketClassCategory = groupBy(
            attendees,
            (attendee) => attendee.ticketClass.category,
        );

        return TICKET_CLASS_CATEGORIES_BY_ORDER.reduce((acc, category) => {
            if (groupAttendeesByTicketClassCategory[category]) {
                acc.push(...groupAttendeesByTicketClassCategory[category]);
            }

            return acc;
        }, []);
    },
);
